import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  RequestAnalyticsService,
  EmailTopStatsDto,
  SmsTopStatsDto,
  EmailCountGroupedByDateDto,
  SmsCountGroupedByDateDto,
  GroupByScale,
} from "../../services/RequestAnalyticsService";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const AnimatedNumber: React.FC<{ value: number }> = ({ value }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const prevValueRef = useRef(value);
  const [isUpdated, setIsUpdated] = useState(false);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      setIsUpdated(true);
      const timer = setTimeout(() => setIsUpdated(false), 1500);
      setDisplayValue(value);
      prevValueRef.current = value;
      return () => clearTimeout(timer);
    }
  }, [value]);

  return (
    <span className={`number-value ${isUpdated ? "value-updated" : ""}`}>
      {displayValue.toLocaleString()}
    </span>
  );
};

type TileKey = "email" | "sms";

interface CustomerStatsDisplayProps {
  customerId: number;
  apiKeyId?: number;
  // Shown at the start of the toolbar, e.g. an API key picker.
  toolbarStart?: React.ReactNode;
}

const CustomerStatsDisplay: React.FC<CustomerStatsDisplayProps> = ({ customerId, apiKeyId, toolbarStart }) => {
  const [emailStats, setEmailStats] = useState<EmailTopStatsDto | null>(null);
  const [smsStats, setSmsStats] = useState<SmsTopStatsDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTile, setSelectedTile] = useState<TileKey | null>(null);
  const [showEmailStats, setShowEmailStats] = useState(true);
  const [showGraph, setShowGraph] = useState(false);

  const [startDate, setStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return d;
  });
  const [groupByScale, setGroupByScale] = useState<GroupByScale>("hour");

  const [emailGraphData, setEmailGraphData] = useState<EmailCountGroupedByDateDto[]>([]);
  const [smsGraphData, setSmsGraphData] = useState<SmsCountGroupedByDateDto[]>([]);

  // Only the latest request may update the screen, so quickly switching API keys
  // never leaves an older key's numbers showing.
  const statsRequest = useRef(0);
  const graphRequest = useRef(0);

  const loadStats = useCallback(async () => {
    if (!customerId || isNaN(customerId)) {
      setError("Invalid customer ID");
      setIsLoading(false);
      return;
    }
    const request = ++statsRequest.current;
    try {
      setRefreshing(true);
      const [email, sms] = await Promise.all([
        RequestAnalyticsService.emails.getStats(apiKeyId ? { apiKeyId } : { customerId }),
        RequestAnalyticsService.sms.getStats(apiKeyId ? { apiKeyId } : { customerId }),
      ]);
      if (request !== statsRequest.current) return;
      setEmailStats(email);
      setSmsStats(sms);
      setError(null);
    } catch (err) {
      if (request !== statsRequest.current) return;
      setError("Failed to load stats");
      console.error("Error loading stats:", err);
    } finally {
      if (request === statsRequest.current) {
        setIsLoading(false);
        setRefreshing(false);
      }
    }
  }, [customerId, apiKeyId]);

  const loadGraphData = useCallback(async () => {
    if (!selectedTile) return;
    const request = ++graphRequest.current;
    try {
      setGraphLoading(true);
      const filters = apiKeyId ? { apiKeyId } : { customerId };
      if (selectedTile === "email") {
        const data = await RequestAnalyticsService.emails.getGroupedStats(
          startDate.toISOString(),
          endDate.toISOString(),
          groupByScale,
          filters
        );
        if (request === graphRequest.current) setEmailGraphData(data);
      } else {
        const data = await RequestAnalyticsService.sms.getGroupedStats(
          startDate.toISOString(),
          endDate.toISOString(),
          groupByScale,
          filters
        );
        if (request === graphRequest.current) setSmsGraphData(data);
      }
    } catch (err) {
      if (request !== graphRequest.current) return;
      setError("Failed to load graph data");
      console.error("Error loading graph data:", err);
    } finally {
      if (request === graphRequest.current) setGraphLoading(false);
    }
  }, [selectedTile, startDate, endDate, groupByScale, customerId, apiKeyId]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (selectedTile) loadGraphData();
  }, [selectedTile, loadGraphData]);

  const handleTileClick = (tile: TileKey) => {
    setSelectedTile(prev => {
      if (prev === tile) { setShowGraph(false); return null; }
      setShowGraph(true);
      return tile;
    });
  };

  const handleGraphToggle = () => {
    setShowGraph(prev => {
      if (!prev && !selectedTile) {
        setSelectedTile(showEmailStats ? "email" : "sms");
      }
      if (prev) setSelectedTile(null);
      return !prev;
    });
  };

  const formatLabel = (timestamp: string) => {
    const date = new Date(timestamp);
    switch (groupByScale) {
      case "minute": return `${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
      case "hour":   return `${date.getHours()}:00`;
      case "day":    return date.toLocaleDateString();
      default:       return date.toLocaleString();
    }
  };

  const emailChartData = {
    labels: emailGraphData.map(d => formatLabel(d.timestamp)),
    datasets: [
      { label: "Processed",        data: emailGraphData.map(d => d.processedCount), borderColor: "rgb(75, 192, 192)",  backgroundColor: "rgba(75, 192, 192, 0.2)",  tension: 0.1, fill: true },
      { label: "Delivered",        data: emailGraphData.map(d => d.deliveredCount), borderColor: "rgb(54, 162, 235)",  backgroundColor: "rgba(54, 162, 235, 0.2)",  tension: 0.1, fill: true },
      { label: "Bounced & Blocked",data: emailGraphData.map(d => d.bouncedCount),   borderColor: "rgb(255, 99, 132)",  backgroundColor: "rgba(255, 99, 132, 0.2)",  tension: 0.1, fill: true },
      { label: "Unique Opens",     data: emailGraphData.map(d => d.opensCount),     borderColor: "rgb(255, 159, 64)",  backgroundColor: "rgba(255, 159, 64, 0.2)",  tension: 0.1, fill: true },
    ],
  };

  const smsChartData = {
    labels: smsGraphData.map(d => formatLabel(d.timestamp)),
    datasets: [
      { label: "Sent",      data: smsGraphData.map(d => d.sentCount),       borderColor: "rgb(75, 192, 192)",  backgroundColor: "rgba(75, 192, 192, 0.2)",  tension: 0.1, fill: true },
      { label: "Delivered", data: smsGraphData.map(d => d.deliveredCount),  borderColor: "rgb(54, 162, 235)",  backgroundColor: "rgba(54, 162, 235, 0.2)",  tension: 0.1, fill: true },
      { label: "Failed",    data: smsGraphData.map(d => d.failedCount),     borderColor: "rgb(255, 99, 132)",  backgroundColor: "rgba(255, 99, 132, 0.2)",  tension: 0.1, fill: true },
      { label: "Pending",   data: smsGraphData.map(d => d.pendingCount),    borderColor: "rgb(153, 102, 255)", backgroundColor: "rgba(153, 102, 255, 0.2)", tension: 0.1, fill: true },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      title: { display: true, text: `Requests per ${groupByScale.charAt(0).toUpperCase() + groupByScale.slice(1)}` },
    },
    scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
  };

  const EmailSummary = () => (
    <div className="d-flex flex-column gap-2">
      {[
        { color: "rgb(75, 192, 192)",  label: "Processed:",        value: emailGraphData.reduce((s, d) => s + d.processedCount, 0) },
        { color: "rgb(54, 162, 235)",  label: "Delivered:",         value: emailGraphData.reduce((s, d) => s + d.deliveredCount, 0) },
        { color: "rgb(255, 99, 132)",  label: "Bounced & Blocked:", value: emailGraphData.reduce((s, d) => s + d.bouncedCount, 0) },
        { color: "rgb(255, 159, 64)",  label: "Unique Opens:",      value: emailGraphData.reduce((s, d) => s + d.opensCount, 0) },
      ].map(({ color, label, value }) => (
        <div key={label} className="d-flex align-items-center justify-content-end">
          <span className="me-2" style={{ width: 12, height: 12, backgroundColor: color, display: "inline-block" }} />
          <span className="text-muted">{label}</span>
          <span className="ms-2 fw-bold">{value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );

  const SmsSummary = () => (
    <div className="d-flex flex-column gap-2">
      {[
        { color: "rgb(75, 192, 192)",  label: "Sent:",      value: smsGraphData.reduce((s, d) => s + d.sentCount, 0) },
        { color: "rgb(54, 162, 235)",  label: "Delivered:", value: smsGraphData.reduce((s, d) => s + d.deliveredCount, 0) },
        { color: "rgb(255, 99, 132)",  label: "Failed:",    value: smsGraphData.reduce((s, d) => s + d.failedCount, 0) },
        { color: "rgb(153, 102, 255)", label: "Pending:",   value: smsGraphData.reduce((s, d) => s + d.pendingCount, 0) },
      ].map(({ color, label, value }) => (
        <div key={label} className="d-flex align-items-center justify-content-end">
          <span className="me-2" style={{ width: 12, height: 12, backgroundColor: color, display: "inline-block" }} />
          <span className="text-muted">{label}</span>
          <span className="ms-2 fw-bold">{value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );

  if (isLoading && !emailStats && !smsStats) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4 shadow-sm" role="alert">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        {error}
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      {/* Toolbar */}
      <div className="d-flex justify-content-end align-items-center gap-2 mb-4" style={{ marginTop: "-1rem" }}>
        {toolbarStart}
        <button
          className="btn btn-outline-primary rounded-circle p-2 d-flex align-items-center justify-content-center"
          onClick={loadStats}
          disabled={refreshing}
          style={{ width: 40, height: 40 }}
        >
          <i className={`bi bi-arrow-repeat ${refreshing ? "spin" : ""}`} style={{ fontSize: "1.2rem" }} />
        </button>
        <button
          className="btn btn-outline-primary rounded-circle p-2 d-flex align-items-center justify-content-center"
          onClick={() => { setSelectedTile(null); setShowEmailStats(prev => !prev); }}
          style={{ width: 40, height: 40 }}
        >
          <i className={`bi ${showEmailStats ? "bi-envelope" : "bi-chat-dots"}`} style={{ fontSize: "1.2rem" }} />
        </button>
        <button
          className={`btn ${showGraph ? "btn-primary" : "btn-outline-primary"} rounded-circle p-2 d-flex align-items-center justify-content-center`}
          onClick={handleGraphToggle}
          style={{ width: 40, height: 40 }}
          title="Toggle graph"
        >
          <i className="bi bi-clipboard-data" style={{ fontSize: "1.2rem" }} />
        </button>
      </div>

      {/* Stat tiles */}
      <div className="row g-4 mb-5">
        {showEmailStats ? (
          <>
            {[
              { icon: "bi-envelope",   label: "Processed Emails",  value: emailStats?.totalProcessedEmailsCount || 0 },
              { icon: "bi-envelope-open", label: "Delivered Emails", value: emailStats?.totalDeliveredEmailsCount || 0 },
              { icon: "bi-envelope-x", label: "Bounced & Blocked",  value: emailStats?.totalBouncedAndBlockedEmails || 0 },
              { icon: "bi-eye",        label: "Unique Opens",       value: emailStats?.totalUniqueOpensEmailsCount || 0 },
            ].map(({ icon, label, value }) => (
              <div className="col" key={label}>
                <div
                  className={`card h-100 border rounded-4 shadow-sm transition-all ${selectedTile === "email" ? "border-primary" : "border-0"}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleTileClick("email")}
                >
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-center align-items-center mb-3">
                      <h6 className="text-uppercase fw-bold text-secondary mb-0">
                        <i className={`bi ${icon} me-2`} />{label}
                      </h6>
                    </div>
                    <div className="d-flex justify-content-center align-items-center">
                      <h2 className="display-4 fw-bold mb-0 text-dark">
                        <AnimatedNumber value={value} />
                      </h2>
                    </div>
                    <div className="text-center mt-2"><small className="text-muted">Total Emails</small></div>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {[
              { icon: "bi-chat-dots",        label: "Sent SMS",       value: smsStats?.totalSentSmsCount || 0 },
              { icon: "bi-chat-square-dots", label: "Delivered SMS",  value: smsStats?.totalDeliveredSmsCount || 0 },
              { icon: "bi-chat-square-text", label: "Failed SMS",     value: smsStats?.totalFailedSmsCount || 0 },
              { icon: "bi-chat-square",      label: "Pending SMS",    value: smsStats?.totalUndeliveredSmsCount || 0 },
            ].map(({ icon, label, value }) => (
              <div className="col" key={label}>
                <div
                  className={`card h-100 border rounded-4 shadow-sm transition-all ${selectedTile === "sms" ? "border-primary" : "border-0"}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleTileClick("sms")}
                >
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-center align-items-center mb-3">
                      <h6 className="text-uppercase fw-bold text-secondary mb-0">
                        <i className={`bi ${icon} me-2`} />{label}
                      </h6>
                    </div>
                    <div className="d-flex justify-content-center align-items-center">
                      <h2 className="display-4 fw-bold mb-0 text-dark">
                        <AnimatedNumber value={value} />
                      </h2>
                    </div>
                    <div className="text-center mt-2"><small className="text-muted">Total SMS</small></div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Chart panel */}
      {showGraph && selectedTile && (
        <div className="card border-0 rounded-4 shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="d-flex gap-3 align-items-end justify-content-start mb-4">
              <div style={{ width: 200 }}>
                <label className="form-label">From Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => date && setStartDate(date)}
                  className="form-control"
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                />
              </div>
              <div style={{ width: 200 }}>
                <label className="form-label">To Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date | null) => date && setEndDate(date)}
                  className="form-control"
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                />
              </div>
              <div style={{ width: 150 }}>
                <label className="form-label">Group By</label>
                <select
                  className="form-select"
                  value={groupByScale}
                  onChange={e => setGroupByScale(e.target.value as GroupByScale)}
                >
                  <option value="minute">Minute</option>
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
              <div className="ms-auto">
                {selectedTile === "email" ? <EmailSummary /> : <SmsSummary />}
              </div>
            </div>
            <div style={{ height: 400 }}>
              {graphLoading ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <Line data={selectedTile === "email" ? emailChartData : smsChartData} options={chartOptions} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const style = document.createElement("style");
style.innerHTML = `
  .transition-all { transition: all 0.3s ease; }
  .number-value { display: inline-block; transition: all 0.5s ease-out; }
  .value-updated { animation: pulse-highlight 1.5s ease-out; }
  @keyframes pulse-highlight {
    0%   { transform: scale(1);   text-shadow: 0 0 0 rgba(0,0,0,0); }
    20%  { transform: scale(1.1); text-shadow: 0 0 10px rgba(0,123,255,0.5); }
    100% { transform: scale(1);   text-shadow: 0 0 0 rgba(0,0,0,0); }
  }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default CustomerStatsDisplay;
